import { Dialog } from '@progress/kendo-react-dialogs';
import { Grid, GridColumn, GridNoRecords, GridPageChangeEvent } from '@progress/kendo-react-grid';
import React from 'react';
import WorkspaceItemContext from '../../app/contexts/workspace/workspace-item-context';
import { useAppDispatch, useAppSelector } from '../../app/hooks/store-hooks';
import { useFlightWorker } from '../../app/hooks/use-flight-worker';
import { useSafeEffect } from '../../app/hooks/use-safe-effect';
import { disableHoldingAnalyzer } from '../../app/state/slices/workspace-slice';
import { MessageType } from '../../services/actor-messages';
import { HoldingAnalyzerFlight } from './types';

export default function HoldingAnalyzer() {
    // TODO: live updates
    const context = React.useContext(WorkspaceItemContext);
    const holdingAnalyzerState = useAppSelector((x) => x.workspace.items[context.id].holdingAnalyzer);
    const objects = useAppSelector((x) => x.drawingObjects.objects);
    const drawingObject = React.useMemo(
        () => (holdingAnalyzerState.active ? objects.find((x) => x.id === holdingAnalyzerState.drawingObjectId) : null),
        [objects, holdingAnalyzerState]
    );
    const [flights, setFlights] = React.useState<HoldingAnalyzerFlight[]>([]);
    const [skip, setSkip] = React.useState(0);
    const dispatch = useAppDispatch();
    const worker = useFlightWorker();

    const loadFlights = React.useCallback(async () => {
        if (!drawingObject) {
            return;
        }
        const flights = await worker.actor.sendAsync({ type: MessageType.getHoldingAnalyzerData, data: drawingObject });
        setFlights(flights);
    }, [worker, drawingObject]);

    useSafeEffect(() => {
        setFlights([]);
        loadFlights();
    }, [drawingObject]);

    if (!holdingAnalyzerState.active || !drawingObject) {
        return null;
    }

    return (
        <Dialog
            width="80vw"
            height="80vh"
            onClose={() => {
                dispatch(disableHoldingAnalyzer({ workspaceId: context.id }));
            }}
            title={'Holding analyzer'}>
            <div className="h-100 d-flex flex-column gap-1">
                <div className="d-flex justify-content-between">
                    <div>
                        Drawing object name: <strong>{drawingObject.name}</strong>
                    </div>
                    <div>Plane count: {flights.length}</div>
                </div>
                <Grid
                    rowHeight={20}
                    className="grid-hide-filter-row fe-small-grid h-100 min-h-0"
                    reorderable={true}
                    resizable={true}
                    skip={skip}
                    pageSize={50}
                    total={flights.length}
                    dataItemKey="id"
                    scrollable={'virtual'}
                    onPageChange={(e: GridPageChangeEvent) => setSkip(e.page.skip)}
                    data={flights}>
                    <GridNoRecords>
                        <div>No flights in object.</div>
                    </GridNoRecords>
                    <GridColumn field="acid" title="ACID" />
                    <GridColumn field="origin" title="Origin" />
                    <GridColumn field="destination" title="Destination" />
                    <GridColumn field="eta" title="ETA(FAA)" className="text-nowrap text-ellipsis" />
                    <GridColumn field="etr" title="ETR(FAA)" />
                    <GridColumn field="altitude" title="Altitude" />
                    <GridColumn
                        field="holding"
                        title="Holding"
                        cells={{ data: (p) => <td {...p.tdProps}>{p.dataItem.holding ? 'Holding' : ''}</td> }}
                    />
                </Grid>
            </div>
        </Dialog>
    );
}
